import React from "react";
import {Box, LinearProgress, Typography} from "@mui/material";
import type {ConsolidationToolProgress} from "~/pages/chat-agent/types/chatTypes";
import {
    progressBarSx,
    progressCaptionSx,
    progressSx,
} from "~/pages/chat-agent/components/AgentActivity/agentActivity.styles";

const STAGE_LABELS: Record<string, {number: number; label: string}> = {
    opportunities: {number: 1, label: "Analyzing opportunities"},
    initiatives: {number: 2, label: "Creating initiatives"},
    persist: {number: 3, label: "Saving and exporting"},
    complete: {number: 4, label: "Finishing up"},
};

function describe(progress: ConsolidationToolProgress): string {
    const stage = STAGE_LABELS[progress.stage];
    const stageNumber = progress.stageNumber != null && progress.stageNumber > 0
        ? progress.stageNumber
        : stage?.number ?? 1;
    const stageTotal = progress.stageTotal != null && progress.stageTotal > 0 ? progress.stageTotal : 4;
    const detail = progress.detail ? ` — ${progress.detail}` : "";
    return `Stage ${stageNumber}/${stageTotal} · ${stage?.label ?? progress.stage} · `
        + `${progress.completed}/${progress.total}${detail}`;
}

type Props = {
    progress?: ConsolidationToolProgress;
};

/** Progress bar for a running tail-spend consolidation tool call. */
export const ConsolidationProgress: React.FC<Props> = ({progress}) => {
    const known = progress && progress.stage !== "done" && progress.total > 0 ? progress : undefined;
    return (
        <Box sx={progressSx}>
            <LinearProgress
                variant={known ? "determinate" : "indeterminate"}
                value={known ? Math.min(100, (known.completed / known.total) * 100) : undefined}
                color="info"
                sx={progressBarSx}
            />
            <Typography variant="caption" color="text.secondary" sx={progressCaptionSx}>
                {known ? describe(known) : "Waiting for progress — this may take several minutes."}
            </Typography>
        </Box>
    );
};
